import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import { append } from '../../utils';

import { CosmosComponent, OptionProps } from '../../types';

export interface TabsProps extends CosmosComponent {
  options: OptionProps[];
  initialValue?: OptionProps;
  hideDefaultOption?: boolean;
  onOptionSelect: (selected: OptionProps) => void;
}

const defaultOption: OptionProps = {
  id: 'defaultOption',
  value: 'All',
};

export const Tabs: FC<TabsProps> = ({
  initialValue,
  options,
  hideDefaultOption = false,
  onOptionSelect,
  testId,
}) => {
  const [selected, setSelected] = useState<OptionProps>(
    initialValue || defaultOption,
  );
  const formattedOptions = [
    ...(hideDefaultOption ? [] : [defaultOption]),
    ...options,
  ];

  const selectOption = (option: OptionProps) => {
    setSelected(option);
    onOptionSelect?.(option);
  };

  return (
    <StyledWrapper>
      {formattedOptions.map((option, index) => (
        <StyledOption
          isSelected={selected?.id === option?.id}
          key={option.id}
          role="button"
          onClick={() => selectOption(option)}
          data-test-id={append(testId, index + 1)}
        >
          {option.value}
        </StyledOption>
      ))}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: inline-flex;
  height: 4rem;
  box-sizing: border-box;
`;

const StyledOption = styled.span<{ isSelected: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.9rem 1.6rem;
  gap: 0.8rem;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 2.2rem;
  height: 4rem;
  color: ${({ theme: { colors } }) => colors?.black2};
  background-color: ${({ theme: { colors } }) => colors?.neutralGray};
  border-radius: 0.2rem 0.2rem 0rem 0rem;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
  margin-right: 0.4rem;
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${({ theme: { colors } }) => colors?.b2cWhite};
      color: ${({ theme: { colors } }) => colors?.r2d2Blue};
      font-weight: 700;
      gap: 1rem;
      box-shadow: inset -0.1rem 0rem 0rem ${({ theme: { colors } }) => colors?.neutralGray},
        inset 0rem 0.1rem 0rem ${({ theme: { colors } }) => colors?.neutralGray},
        inset 0.1rem 0rem 0rem ${({ theme: { colors } }) => colors?.neutralGray};
      border-left: 0.1rem solid
        ${({ theme: { colors } }) => colors?.neutralGray};
      border-right: 0.1rem solid
        ${({ theme: { colors } }) => colors?.neutralGray};
      border-top: 0.1rem solid ${({ theme: { colors } }) => colors?.neutralGray};
      border-radius: 0.2rem 0.2rem 0rem 0rem;
      flex-direction: row;
      flex: none;
      flex-grow: 0;
    `}
`;
